import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ModuleConfig, ModuleConfigUpdatePayload } from 'pages/AnalyticsV2/types';
import experienceGraphService from 'services/api/experience-graph.api';

type UseExperienceGraphResult = {
  graph: ModuleConfig | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  handleUpdateGraph: (updatedGraph: ModuleConfigUpdatePayload) => void;
  isGraphUpdating: boolean;
  isGraphUpdated: boolean;
  graphUpdateError: string;
};

export default function useExperienceGraph(id: string): UseExperienceGraphResult {
  const queryClient = useQueryClient();
  const queryKey = ['experience-graph', { id }];
  const [graphUpdateError, setGraphUpdateError] = useState('');

  const {
    data: graph,
    isLoading,
    isFetching,
    isError,
  } = useQuery<ModuleConfig>({
    queryKey,
    queryFn: () => experienceGraphService.getExperienceGraph(id),
    refetchOnWindowFocus: false,
    enabled: !!id,
  });

  // Mutation to update the graph
  const mutation = useMutation(experienceGraphService.updateExperienceGraph, {
    onMutate: async (updatedModuleConfig) => {
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous moduleconfig
      const previousData = queryClient.getQueryData<ModuleConfig>(queryKey);

      // Optimistically update the UI before backend responds
      queryClient.setQueryData<ModuleConfig>(queryKey, (oldModuleConfig) => {
        if (!oldModuleConfig) {
          return;
        }

        return {
          ...oldModuleConfig,
          ...(updatedModuleConfig as ModuleConfig),
        };
      });

      return { previousData };
    },
    onError: (error: any, updatedModuleConfig, context) => {
      // Rollback to the previous state
      queryClient.setQueryData(queryKey, context?.previousData);

      let errorMessage = isAxiosError(error) || error instanceof Error ? error.message : 'unknown';
      if (error?.response?.data?.error?.exceptionMessage) {
        errorMessage = error.response.data.error.exceptionMessage;
      }

      setGraphUpdateError(
        `The custom analytics graph ${updatedModuleConfig.name} could not be edited. ${errorMessage}`,
      );
    },
    onSettled: () => {
      // Refetch the moduleconfig to ensure consistency
      queryClient.invalidateQueries(queryKey);
    },
  });

  const handleUpdateGraph = useCallback(
    (updatedModuleConfig: ModuleConfigUpdatePayload) => mutation.mutate(updatedModuleConfig),
    [mutation],
  );

  return {
    graph,
    isLoading,
    isError,
    isFetching,
    handleUpdateGraph,
    isGraphUpdating: mutation.isLoading,
    isGraphUpdated: mutation.isSuccess,
    graphUpdateError,
  };
}
